import { useState, useEffect, useRef } from "react";
import { Zap, Award, ArrowRight, Gauge, Database } from "lucide-react";
import { PROVIDER_META, findModel, modelsForKind } from "@/lib/meta";

export const RecommendationPanel = ({ catalog, category, onStart }) => {
  const recs = category.recommendations;
  const [providerId, setProviderId] = useState(recs[0].provider);
  const [modelId, setModelId] = useState(recs[0].model);
  const ref = useRef(null);

  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, []);

  const kind = category.kind || "text";
  const usable = catalog.providers.filter((p) => modelsForKind(p, kind).length > 0);
  const seen = new Set();
  const rankedProviders = [
    ...recs.map((r) => usable.find((p) => p.id === r.provider)),
    ...usable.filter((p) => !recs.some((r) => r.provider === p.id)),
  ].filter((p) => p && !seen.has(p.id) && seen.add(p.id));

  const provider = usable.find((p) => p.id === providerId);
  const providerModels = modelsForKind(provider, kind);
  const rec = recs.find((r) => r.provider === providerId);
  const model = findModel(catalog, providerId, modelId);

  const selectProvider = (pid) => {
    setProviderId(pid);
    const r = recs.find((x) => x.provider === pid);
    setModelId(r ? r.model : modelsForKind(usable.find((p) => p.id === pid), kind)[0].id);
  };

  return (
    <div ref={ref} className="rise scroll-mt-20" data-testid="recommendation-panel">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Step 2 · Choose a provider</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rankedProviders.map((p, i) => {
          const meta = PROVIDER_META[p.id];
          const Icon = meta.icon;
          const r = recs.find((x) => x.provider === p.id);
          const active = p.id === providerId;
          return (
            <button key={p.id} data-testid={`provider-card-${p.id}`} onClick={() => selectProvider(p.id)}
              className={`text-left p-5 rounded-2xl border transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 ${active ? `bg-[#141726] ${meta.ring} ${meta.glow}` : "bg-[#11131E] border-slate-800/80 hover:border-slate-700"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <Icon className={`w-5 h-5 ${meta.color}`} />
                  <span className="font-display font-semibold text-slate-100">{p.name}</span>
                </div>
                {i === 0 && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><Award className="w-3 h-3" /> Best match</span>}
                {i > 0 && r && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-700/40 text-slate-300 border border-slate-600/40">#{i + 1} pick</span>}
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">{r ? r.reason : p.tagline}</p>
            </button>
          );
        })}
      </div>

      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mt-10 mb-4">Step 3 · Pick the model</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="model-list">
        {providerModels.map((m) => {
          const active = m.id === modelId;
          const recommended = recs.some((r) => r.provider === providerId && r.model === m.id);
          return (
            <button key={m.id} data-testid={`model-option-${m.id}`} onClick={() => setModelId(m.id)}
              className={`text-left px-5 py-4 rounded-xl border flex items-center justify-between gap-4 transition-colors ${active ? "bg-indigo-500/10 border-indigo-500/60" : "bg-[#11131E] border-slate-800/80 hover:border-slate-700 hover:bg-[#141726]"}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">{m.name}</span>
                  {recommended && <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 flex items-center gap-1"><Zap className="w-3 h-3" /> Recommended</span>}
                </div>
                <p className="text-xs text-slate-400 mt-1">{m.tag}</p>
              </div>
              <div className="text-right text-[11px] text-slate-500 shrink-0 space-y-1">
                <p className="flex items-center gap-1 justify-end"><Gauge className="w-3 h-3" /> {m.speed}</p>
                <p className="flex items-center gap-1 justify-end font-mono"><Database className="w-3 h-3" /> {m.context}</p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-8 p-5 rounded-2xl bg-gradient-to-r from-indigo-950/50 via-[#141726] to-[#11131E] border border-indigo-500/30 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="start-session-panel">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">Ready to launch</p>
          <p className="text-slate-100 font-display font-semibold">{category.label} · <span className={PROVIDER_META[providerId].color}>{provider.name}</span> · {model?.name}</p>
          <p className="text-xs text-slate-400 mt-1">You can switch models any time during the conversation.</p>
        </div>
        <button data-testid="start-session-btn" onClick={() => onStart({ category: category.id, provider: providerId, model: modelId })}
          className="shrink-0 inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-5 py-2.5 rounded-xl transition-colors">
          Start with {model?.name} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
